<script>
import { defineComponent, ref, computed, watch } from 'vue'
import { useQuestionKeyboardNav } from '@/composables/useQuestionKeyboardNav'

export default defineComponent({
  name: 'ZoneQuestion',
  props: {
    zones: {
      type: Array,
      required: true
    },
    previousAnswer: {
      type: Number,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    /* Anchor roles (PD-1258): when the CEO ceiling has removed levels from the
       ladder, say so inline rather than silently offering a shorter list. Null
       when no ceiling is set or it changed nothing. */
    ceilingNote: {
      type: String,
      default: null
    },
    /* The published grade link (PD-1293), as the parent resolved it against this
       track: { levelIndex, prefillText, infoText }. levelIndex null means the link
       exists but cannot be offered here, and infoText says why. */
    suggestion: {
      type: Object,
      default: null
    }
  },
  emits: ['answer', 'back', 'restart', 'delete'],
  setup(props, { emit }) {
    const allLevels = computed(() => {
      if (!props.zones.length) return []
      const seen = new Set()
      const levels = []
      for (const zone of props.zones) {
        for (const lvl of zone.levels) {
          if (!seen.has(lvl.level)) {
            seen.add(lvl.level)
            levels.push(lvl)
          }
        }
      }
      return levels
    })

    // Convert previous zone index to a level index (center of that zone)
    function zoneIndexToLevelIndex(zi) {
      if (zi === null || zi === undefined) return null
      // Zone i covers levels i, i+1, i+2 — center is i+1
      return Math.min(zi + 1, allLevels.value.length - 1)
    }

    function levelIndexToZoneIndex(li) {
      if (li === null) return 0
      return Math.max(0, Math.min(li - 1, props.zones.length - 1))
    }

    /* The suggestion only fills an EMPTY step, and only until the person picks: a
       zone they already chose, or one they are choosing now, always wins. It is a
       pre-selection, never an answer — `answer` is emitted by Next and nothing else,
       so a suggested zone is applied only once someone confirms it. */
    const suggestedIndex = computed(() => props.suggestion?.levelIndex ?? null)
    const touched = ref(false)
    const initialIndex = () => (props.previousAnswer !== null && props.previousAnswer !== undefined
      ? zoneIndexToLevelIndex(props.previousAnswer)
      : suggestedIndex.value)

    const selectedLevelIndex = ref(initialIndex())

    watch(() => props.previousAnswer, () => {
      touched.value = false
      selectedLevelIndex.value = initialIndex()
    })
    // The link is fetched alongside the journey and can land after this step renders.
    watch(suggestedIndex, () => {
      if (!touched.value) selectedLevelIndex.value = initialIndex()
    })

    const hasAnswer = computed(() => props.previousAnswer !== null && props.previousAnswer !== undefined)
    // Pre-filled, and still sitting where the link put it.
    const onSuggestion = computed(() =>
      !hasAnswer.value && suggestedIndex.value !== null && selectedLevelIndex.value === suggestedIndex.value)
    const suggestionText = computed(() => {
      if (!props.suggestion) return null
      if (!hasAnswer.value && suggestedIndex.value !== null) return props.suggestion.prefillText
      return props.suggestion.infoText
    })

    const hoveredLevelIndex = ref(null)

    function getZoneIndices(centerIndex) {
      if (centerIndex === null || centerIndex === undefined) return []
      const max = allLevels.value.length - 1
      const start = Math.max(0, centerIndex - 1)
      const end = Math.min(max, centerIndex + 1)
      const indices = []
      for (let i = start; i <= end; i++) indices.push(i)
      return indices
    }

    const hoveredZoneIndices = computed(() => getZoneIndices(hoveredLevelIndex.value))
    const selectedZoneIndices = computed(() => getZoneIndices(selectedLevelIndex.value))

    const zoneLevels = computed(() => {
      if (selectedLevelIndex.value === null) return []
      return selectedZoneIndices.value.map(i => allLevels.value[i])
    })

    function isInHoverZone(index) {
      return hoveredZoneIndices.value.includes(index)
    }

    function isSelected(index) {
      return selectedZoneIndices.value.includes(index)
    }

    function selectLevel(index) {
      touched.value = true
      selectedLevelIndex.value = index
    }

    function submitAnswer() {
      emit('answer', levelIndexToZoneIndex(selectedLevelIndex.value))
    }

    const { keyHint } = useQuestionKeyboardNav({
      options: () => allLevels.value.map((l, index) => ({ key: Number(l.abbreviation?.match(/\d+/)?.[0]) ?? index, value: index })),
      onSelect: selectLevel,
      onNext: submitAnswer,
      onPrev: () => emit('back'),
      canNext: () => selectedLevelIndex.value !== null,
      canNavigate: () => !props.editing,
      verb: 'select',
    })

    return {
      allLevels,
      selectedLevelIndex,
      hoveredLevelIndex,
      zoneLevels,
      isInHoverZone,
      isSelected,
      selectLevel,
      submitAnswer,
      keyHint,
      suggestedIndex,
      onSuggestion,
      suggestionText,
    }
  }
})
</script>

<template>
  <div class="lvl-eval">
    <div class="lvl-qcard">
      <div class="lvl-qcard-in">
        <!-- Label -->
        <div class="lvl-q-eyebrow">Classification</div>

        <!-- Title -->
        <h2 class="lvl-q-title">Select a level zone</h2>

        <!-- Description -->
        <p class="lvl-q-desc">Choose the range of levels that best fits seniority of this role. You'll review specific descriptors in the next step.</p>

        <!-- Anchor roles (PD-1258): why the top of the ladder is missing. -->
        <div v-if="ceilingNote" class="lvl-note lvl-note--info lvl-anchor-ceilnote">
          <span class="ms" style="font-size:16px">anchor</span>
          <span>{{ ceilingNote }}</span>
        </div>

        <!-- Published grade link (PD-1293): why a zone is already selected, or why
             the link could not be offered on this track. -->
        <div v-if="suggestionText" class="lvl-note lvl-note--info lvl-zonesugg" data-testid="classification-zone-suggestion">
          <span class="ms" style="font-size:16px">link</span>
          <span>{{ suggestionText }}</span>
        </div>

        <!-- Zone segmented bar -->
        <div class="lvl-zonebar" role="radiogroup">
          <button
            v-for="(lvl, index) in allLevels"
            :key="lvl.level"
            type="button"
            role="radio"
            :aria-checked="selectedLevelIndex === index"
            class="lvl-zoneseg"
            :class="{
              in: (hoveredLevelIndex !== null && isInHoverZone(index)) || isSelected(index),
              center: selectedLevelIndex === index,
              sugg: suggestedIndex === index
            }"
            :title="suggestedIndex === index ? 'Suggested by your published grade link' : null"
            @mouseenter="hoveredLevelIndex = index"
            @mouseleave="hoveredLevelIndex = null"
            @click="selectLevel(index)"
            :data-testid="'classification-zone-option-' + lvl.level"
          >
            {{ lvl.abbreviation }}
          </button>
        </div>

        <!-- Selected zone detail -->
        <div v-if="zoneLevels.length" class="lvl-zonereveal">
          <div class="lvl-zonereveal-head">Levels {{ zoneLevels[0].abbreviation }}–{{ zoneLevels[zoneLevels.length - 1].abbreviation }}</div>
          <div
            v-for="lvl in zoneLevels"
            :key="lvl.level"
            class="lvl-zonelvl"
            :class="{ sel: allLevels[selectedLevelIndex] && lvl.level === allLevels[selectedLevelIndex].level }"
          >
            <div class="lvl-zonelvl-top">
              <span class="lvl-zonelvl-code">Level {{ lvl.abbreviation }}</span>
              <span class="lvl-zonelvl-name">{{ lvl.title }}</span>
            </div>
            <div class="lvl-zonelvl-desc">{{ lvl.description }}</div>
          </div>
          <div class="lvl-zonereveal-note">The centre level is your best fit; the levels either side define the zone you'll review next.</div>
        </div>
      </div>

      <!-- Footer -->
      <div class="lvl-cardfoot">
        <span class="restart">
          <button type="button" class="lvl-btn lvl-btn--outlined" :disabled="readOnly" @click="$emit('restart')">Restart</button>
          <button v-if="canDelete" type="button" class="lvl-btn lvl-btn--danger" :disabled="readOnly" @click="$emit('delete')">Delete</button>
        </span>
        <span class="prog">
          <template v-if="zoneLevels.length">Levels {{ zoneLevels[0].abbreviation }}–{{ zoneLevels[zoneLevels.length - 1].abbreviation }}</template>
          <template v-else>Select a level zone to narrow the evaluation</template>
          <template v-if="keyHint"> · {{ keyHint }}</template>
        </span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--outlined" @click="$emit('back')">Previous</button>
          <!-- On an untouched pre-fill the button says what pressing it does. -->
          <button type="button" class="lvl-btn" :disabled="selectedLevelIndex === null" @click="submitAnswer">{{ onSuggestion ? 'Confirm zone' : 'Next' }} <span class="ms">arrow_forward</span></button>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>
